import { mergeRefs, useControllableState } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import Button from 'ui/Button';
import { GroupProps } from 'ui/Group';
import Input, { InputGroup, InputProps } from 'ui/Input';
import { Box, HStack, StackProps, VStack } from 'ui/Layout';

import Icon from './Icon';

export type PasswordVisibilityProps = {
  defaultVisible?: boolean;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visibilityIcon?: { on: React.ReactNode; off: React.ReactNode };
};

export type PasswordInputProps = InputProps &
  PasswordVisibilityProps & {
    rootProps?: GroupProps;
  };

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (props, ref) => {
    const {
      rootProps,
      defaultVisible,
      visible: visibleProp,
      onVisibleChange,
      visibilityIcon = {
        on: <Icon icon="eye" />,
        off: <Icon icon="eye-slash" />,
      },
      ...rest
    } = props;

    const [visible, setVisible] = useControllableState({
      value: visibleProp,
      defaultValue: defaultVisible || false,
      onChange: onVisibleChange,
    });

    const inputRef = React.useRef<HTMLInputElement>(null);

    return (
      <InputGroup
        width="full"
        endElement={
          <Button
            aspectRatio="square"
            background="transparent"
            disabled={rest.disabled}
            height="calc(100% - {spacing.2})"
            me={-2}
            onPointerDown={(e) => {
              if (rest.disabled) return;
              if (e.button !== 0) return;
              e.preventDefault();
              setVisible(!visible);
            }}
            size="sm"
            tabIndex={-1}
            variant="neutral-ghost"
          >
            {visible ? visibilityIcon.off : visibilityIcon.on}
          </Button>
        }
        {...rootProps}
      >
        <Input
          {...rest}
          ref={mergeRefs(ref, inputRef)}
          type={visible ? 'text' : 'password'}
        />
      </InputGroup>
    );
  }
);

type PasswordStrengthMeterProps = StackProps & {
  password: string;
};

export const PasswordStrengthMeter = React.forwardRef<
  HTMLDivElement,
  PasswordStrengthMeterProps
>(({ password, ...rest }, ref) => {
  const { value, label, color } = useMemo(
    () => getPasswordStrength(password),
    [password]
  );

  if (!password) {
    return null;
  }

  return (
    <VStack align="flex-end" gap={1} mt={1} w="100%" ref={ref} {...rest}>
      <HStack width="full" ref={ref} {...rest}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Box
            key={index}
            height="1"
            flex="1"
            rounded="sm"
            data-selected={index < value ? '' : undefined}
            bg="gray.300"
            _selected={{
              bg: color,
            }}
          />
        ))}
      </HStack>
      {label && <HStack textStyle="xs">{label}</HStack>}
    </VStack>
  );
});

// Inspired by https://github.com/deanilvincent/check-password-strength/blob/master/src/index.js
function getPasswordStrength(password: string) {
  const length = password.length;
  const diversity = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].reduce(
    (acc, test) => (test.test(password) ? acc + 1 : acc),
    0
  );
  return (
    [
      {
        minDiversity: 4,
        minLength: 12,
        label: 'Strong',
        color: 'green.600',
        value: 4,
      },
      {
        minDiversity: 4,
        minLength: 10,
        label: 'Good',
        color: 'green.500',
        value: 3,
      },
      {
        minDiversity: 2,
        minLength: 8,
        label: 'Average',
        color: 'yellow.500',
        value: 2,
      },
    ].find(
      (threshold) =>
        length >= threshold.minLength && diversity >= threshold.minDiversity
    ) ?? {
      minDiversity: 0,
      minLength: 0,
      label: 'Weak',
      color: 'red.500',
      value: 1,
    }
  );
}

export default PasswordInput;
